import { ChevronDownIcon } from '@chakra-ui/icons'
import { Badge, Box, Menu, MenuButton, MenuItem, MenuList, Spinner } from '@chakra-ui/react'
import { useMemo, useState } from 'react'

import { StatusBadge } from '../../../components/StatusBadge'
import { useAppToast } from '../../../helpers'
import { IScheduleClass, TScheduleStatus } from '../../../services/ScheduleClasses/interfaces'
import ScheduleClassService from '../../../services/ScheduleClasses/ScheduleClassService'
import { SCHEDULE_STATUS } from './constants'

interface IProps {
  scheduleClass: IScheduleClass
  canEdit: boolean
  /**
   * Chamado só depois de a API confirmar, com o status que ela GRAVOU — o pai é quem decide se
   * repinta a linha ou a lista.
   *
   * O gravado pode não ser o escolhido: o backend confirma a turma a partir de metade das vagas
   * e a esgota quando elas acabam (ABRA-315). Propagar a escolha deixaria o badge dizendo
   * "Pendente" numa turma que o servidor manteve em "Vagas Esgotadas".
   */
  onChanged: (status: TScheduleStatus) => void
}

/**
 * Status da turma na listagem, editável no próprio badge (ABRA-181).
 *
 * Status é o campo que mais muda no dia a dia — turma confirma, lota, é cancelada — e abrir o
 * modal inteiro para trocar um select era o caminho mais longo da tela. O menu resolve em dois
 * cliques, sem estado de formulário.
 *
 * O que se vê continua sendo o próprio Badge, e não um botão sólido como em RequestsPage: a cor
 * do badge é o que o olho varre nesta tabela, e uma coluna inteira de botões competiria com ela.
 * Quem recebe o clique e o foco, porém, é um `<button>` de verdade em volta dele.
 *
 * O menu oferece os cinco status, inclusive os dois que o backend também decide sozinho: dá
 * para antecipar uma confirmação, mas não para desfazê-la enquanto a ocupação mandar o
 * contrário — nesse caso o badge simplesmente volta ao valor que o servidor gravou.
 */
export default function ScheduleClassStatusCell({ scheduleClass, canEdit, onChanged }: IProps) {
  const toast = useAppToast()
  const service = useMemo(() => new ScheduleClassService(), [])
  const [saving, setSaving] = useState(false)

  const status = scheduleClass.status
  // O mapa é exaustivo para `TScheduleStatus`, mas quem responde é o backend: um status novo lá
  // chegaria aqui como `undefined` e derrubaria a tabela inteira num `TypeError`. O ramo abaixo
  // já degrada sozinho porque o `StatusBadge` tem esse fallback embutido; este espelha o dele.
  const { label, colorScheme } = SCHEDULE_STATUS[status] ?? { label: status, colorScheme: 'gray' }

  if (!canEdit) {
    return <StatusBadge status={status} statuses={SCHEDULE_STATUS} />
  }

  async function handleSelect(next: TScheduleStatus) {
    if (next === status || saving) return

    setSaving(true)
    try {
      onChanged(await service.updateStatus(scheduleClass.id, next))
    } catch {
      toast.error('Erro ao atualizar o status')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Menu placement="bottom-start" isLazy>
      {/* O `MenuButton` fica sendo o `<button>` que ele já é por padrão, e o selo vai dentro:
          com `as={Badge}` o gatilho virava um `<span>`, fora da ordem de tabulação e sem abrir
          por teclado — o único `MenuButton` do painel que não era um botão. */}
      <MenuButton type="button" title="Alterar status" aria-label={`Alterar status (atual: ${label})`}>
        <Badge colorScheme={colorScheme} cursor="pointer" display="inline-flex" alignItems="center" gap={1}>
          {label}
          {saving ? <Spinner size="xs" /> : <ChevronDownIcon />}
        </Badge>
      </MenuButton>

      <MenuList minW="180px">
        {Object.entries(SCHEDULE_STATUS).map(([value, { label, colorScheme }]) => (
          <MenuItem
            key={value}
            onClick={() => handleSelect(value as TScheduleStatus)}
            icon={<Box w={2} h={2} borderRadius="full" bg={`${colorScheme}.500`} />}
            fontWeight={value === status ? 'bold' : 'normal'}
          >
            {label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  )
}
